import { httpClient } from '@/modules/common/services/http/http-client';
import { TradeInstrumentPositionPrice } from '@/modules/trade/types/instrument';
import {
  PositionsParams,
  GetPositionsResponse,
  GetWatchlistParams,
  GetWatchlistResponse,
  IInstrumentView,
  CreateOrderFeedback,
  CreateOrderBody,
  PositionsView,
  Instrument,
  InstrumentHistorical,
  PositionsSummaryResponse,
  GetStockLendingListParams,
  GetStockLendingListResponse,
  GetOptionsListParams,
  GetOptionsListResponse,
  GetForwardListResponse,
  GetForwardListParams,
  GetCustomOperationsListResponse,
  GetCustomOperationsListParams,
  GetHistoricalOptionsListParams,
  GetHistoricalOptionsListResponse,
  GetInstrumentPositionHistoricalResponse,
  GetInstrumentChartResponse,
  InstrumentPrice,
  InstrumentStatus,
  AcceptOutsideRiskProfileTerm,
  ValidateOrderBody,
  ValidateOrderResponse
} from './types';

const baseURL = `${process.env.VUE_APP_BFF_URL}/trade`;
export const httpClientWithDefaultOptions = httpClient.withOptions({ baseURL });
export const bffHttpClient = httpClient.withOptions({
  baseURL: `${process.env.VUE_APP_BFF_URL}/trading`
});

export function addToWatchlist(symbol: string): Promise<{}> {
  return httpClientWithDefaultOptions.post('/v1/updateWatchlist', {
    symbol
  });
}

export function deleteFromWatchlist(symbol: string): Promise<{}> {
  return httpClientWithDefaultOptions.delete(`/v1/watchlist/${symbol}`);
}

export function getPositions(
  params: PositionsParams,
  abortSignal?: AbortSignal
): Promise<GetPositionsResponse> {
  return httpClientWithDefaultOptions.get(`/v1/positions`, {
    params,
    abortSignal
  });
}

export function getInstrumentPosition(
  symbol: string,
  abortSignal?: AbortSignal
): Promise<TradeInstrumentPositionPrice> {
  return httpClientWithDefaultOptions.get('/v1/instrumentPosition', {
    params: {
      symbol
    },
    abortSignal
  });
}

export function getPositionsView(
  params: PositionsParams,
  abortSignal?: AbortSignal
): Promise<PositionsView<Instrument>> {
  return httpClientWithDefaultOptions.get(`/v1/myAssetsView`, {
    params,
    abortSignal
  });
}

export function getPositionsHistoricalView(
  params: PositionsParams,
  abortSignal?: AbortSignal
): Promise<PositionsView<InstrumentHistorical>> {
  return httpClientWithDefaultOptions.get(`/v1/myAssetsHistoricalView`, {
    params,
    abortSignal
  });
}

export function getWatchlist(
  params: GetWatchlistParams
): Promise<GetWatchlistResponse> {
  return httpClientWithDefaultOptions.get('/v1/getWatchlist', {
    params
  });
}

export function getInstrumentDetailView(
  ticker: string
): Promise<IInstrumentView> {
  return httpClientWithDefaultOptions.get('/v1/instrumentDetailView', {
    params: {
      ticker
    }
  });
}

export function createOrder(
  body: CreateOrderBody
): Promise<CreateOrderFeedback> {
  return httpClientWithDefaultOptions.post('/v1/createOrder', body);
}

export function getPositionsSummary(): Promise<PositionsSummaryResponse> {
  return httpClientWithDefaultOptions.get('/v1/homePosition');
}

export function getStockLendingList(
  params: GetStockLendingListParams,
  abortSignal?: AbortSignal
): Promise<GetStockLendingListResponse> {
  return httpClientWithDefaultOptions.get('/v1/positions/btcList', {
    params,
    abortSignal
  });
}

export function getOptionsList(
  params: GetOptionsListParams,
  abortSignal?: AbortSignal
): Promise<GetOptionsListResponse> {
  return httpClientWithDefaultOptions.get('/v1/positions/options', {
    params,
    abortSignal
  });
}

export function getHistoricalOptionsList(
  params: GetHistoricalOptionsListParams,
  abortSignal?: AbortSignal
): Promise<GetHistoricalOptionsListResponse> {
  return httpClientWithDefaultOptions.get('/v1/positions/historicalOptions', {
    params,
    abortSignal
  });
}

export function getForwardList(
  params: GetForwardListParams,
  abortSignal?: AbortSignal
): Promise<GetForwardListResponse> {
  return httpClientWithDefaultOptions.get('/v1/positions/forwardList', {
    params,
    abortSignal
  });
}

export function getCustomOperationsList(
  params: GetCustomOperationsListParams,
  abortSignal?: AbortSignal
): Promise<GetCustomOperationsListResponse> {
  return httpClientWithDefaultOptions.get('/v1/positions/customOperations', {
    params,
    abortSignal
  });
}

export function getInstrumentPositionHistorical(
  symbol: string,
  abortSignal?: AbortSignal
): Promise<GetInstrumentPositionHistoricalResponse> {
  return httpClientWithDefaultOptions.get('/v1/instrumentPositionHistorical', {
    params: {
      symbol
    },
    abortSignal
  });
}

export function getInstrumentChart(
  symbol: string,
  filter?: string | null,
  abortSignal?: AbortSignal
): Promise<GetInstrumentChartResponse> {
  return bffHttpClient.get('/v1/assets/chart', {
    params: {
      symbol,
      filter
    },
    abortSignal
  });
}

export function getInstrumentPrice(symbol: string): Promise<InstrumentPrice> {
  return httpClientWithDefaultOptions.get('/v1/instrumentPrice', {
    params: {
      symbol
    }
  });
}

export function getInstrumentStatus(
  ticker: string
): Promise<{ instrument: InstrumentStatus }> {
  return httpClientWithDefaultOptions.get('/v1/instrumentStatus', {
    params: {
      ticker
    }
  });
}

export function acceptOutsideRiskProfileTerm(
  symbol: string
): Promise<AcceptOutsideRiskProfileTerm> {
  return httpClientWithDefaultOptions.post('/v1/acceptOutsideRiskProfileTerm', {
    symbol
  });
}

export function validateOrder(
  body: ValidateOrderBody
): Promise<ValidateOrderResponse> {
  return httpClientWithDefaultOptions.post('/v2/validateOrder', body);
}
